import { type ReactNode, useCallback, useRef, useState } from 'react'
import type { ParagraphFeedback } from './content-brief.interface'
import type { ShadowBlockEntry } from './content-brief-shadow-blocks.helper'
import { ContentBriefShadowCommentPopover } from './content-brief-shadow-comment-popover.molecule'
import { ContentBriefShadowRenderer } from './content-brief-shadow-renderer.molecule'
import { useContentBriefShadowInteraction } from './use-content-brief-shadow-interaction.hook'

export interface ContentBriefShadowViewProps {
  htmlContent: string
  commentMap: Record<number, string>
  onCommentChange: (blockIndex: number, comment: string | undefined) => void
  onBlocksReady: (feedbackBlockList: ParagraphFeedback[]) => void
}

const blocksToFeedbackBase = (blockList: ShadowBlockEntry[]): ParagraphFeedback[] =>
  blockList.map((block) => ({
    lineNumber: block.index + 1,
    text: block.textContent,
    comment: '',
  }))

export const ContentBriefShadowView: React.FC<ContentBriefShadowViewProps> = ({
  htmlContent,
  commentMap,
  onCommentChange,
  onBlocksReady,
}): ReactNode => {
  const hostRef = useRef<HTMLDivElement>(null)
  const [, setBlockList] = useState<ShadowBlockEntry[]>([])

  const handleBlocksReady = useCallback(
    (list: ShadowBlockEntry[]): void => {
      setBlockList(list)
      onBlocksReady(blocksToFeedbackBase(list))
    },
    [onBlocksReady]
  )

  const { activeBlockIndex, activeBlockRect, closeActiveBlock } = useContentBriefShadowInteraction({
    hostRef,
    commentMap,
  })

  return (
    <div className="content-brief-shadow-view relative h-full min-h-0 flex-1">
      <ContentBriefShadowRenderer
        htmlContent={htmlContent}
        hostRef={hostRef}
        onBlocksReady={handleBlocksReady}
      />
      <ContentBriefShadowCommentPopover
        blockIndex={activeBlockIndex}
        blockRect={activeBlockRect}
        comment={activeBlockIndex !== null ? commentMap[activeBlockIndex] : undefined}
        onCommentChange={onCommentChange}
        onClose={closeActiveBlock}
      />
    </div>
  )
}
